import { HttpClient, HttpParams } from '@angular/common/http';
import type { HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as Global from '../global/constants';
import { AbmDataMapping, AbmHttpMethod, AbmRequestLink, AbmResponseData, AbmUser } from '../global/types';
import { AbmFilePaths, AbmFilePathType } from '../utils/abm-file-path';
import { AbmUtil } from '../Utils/abm-util';
import { AbmConfigService } from './abm-config.service';

export enum DATA_STATUS {
    NORMAL,
    WARN,
    ERROR,
}

export const ABM_REQUESTS = new InjectionToken<AbmRequestLink[]>('ABM_REQUESTS');

@Injectable({
    providedIn: 'root'
})
export class AbmDataService {

    private _apiUrl: string;
    private _useMock: boolean;

    public dataStatus$: ReplaySubject<DATA_STATUS> = new ReplaySubject();
    private _isCheckingErr: boolean;
    private _dataErrorCount: Map<string, number>;
    private _requestMap: Map<string, AbmRequestLink>;

    constructor(
        private http: HttpClient,
        private configService: AbmConfigService,
        @Inject(ABM_REQUESTS) private _requests?: AbmRequestLink[],
    ) {
        this._initApiUrl();
        this._dataErrorCount = new Map();
        this._isCheckingErr = false;

        this.setRequests(_requests);
    }

    private _initApiUrl() {
        const dataServerConfig = this.configService.getDataServerConfig();
        const beConfig = this.configService.getBeConfig();
        this._useMock = this.configService.config.useMock || false;

        const mockDir = AbmFilePaths.getPath(AbmFilePathType.MOCK);

        if (dataServerConfig.apiEnable) {
            // 此时表示有单独的 API 服务。
            this._apiUrl = this._useMock ? mockDir : AbmUtil.formPath(dataServerConfig.api, dataServerConfig.url);
        } else if (beConfig.apiEnable) {
            // 这里表示与 BE 共用 API 服务。
            this._apiUrl = this._useMock ? mockDir : AbmUtil.formPath(beConfig.api, beConfig.url);
        } else {
            throw new Error('No data API configured, please check your config.json file');
        }

    }

    private _countDataApiStatus(endpoint: string, isError: boolean) {
        const count = this._dataErrorCount.get(endpoint) || 0;
        if (isError) {
            this._dataErrorCount.set(endpoint, count + 1);
        } else {
            this._dataErrorCount.set(endpoint, 0);
        }
        if (!this._isCheckingErr) {
            this._checkError();
        }
    }

    private _checkError() {
        this._isCheckingErr = true;
        setTimeout(() => {
            const errorCount = Array.from(this._dataErrorCount.values()).reduce((a, b) => a + b, 0);
            if (errorCount > 2) {
                this.dataStatus$.next(DATA_STATUS.ERROR);
            } else if (errorCount > 0) {
                this.dataStatus$.next(DATA_STATUS.WARN);
            } else {
                this.dataStatus$.next(DATA_STATUS.NORMAL);
            }
            this._isCheckingErr = false;
        }, Math.round(this.configService.config.refreshInterval / 3));

    }

    public setRequests(requests: AbmRequestLink[]) {
        if (requests?.length > 0) {
            if (!this._requestMap) {
                this._requestMap = new Map();
            }
            requests.forEach(r => this._requestMap.set(r.indexName, r));
        }
    }

    public addRequest(request: AbmRequestLink): AbmRequestLink {
        this.setRequests([request]);
        return request;
    }

    public getRequest(indexName: string, newName?: string): AbmRequestLink {
        const request = this._requestMap.get(indexName);
        if (!request) {
            throw new Error('Can not find request for:' + indexName);
        } else {
            return newName ? request.setName(newName) : request;
        }
    }

    public clearStatus() {
        this._dataErrorCount.clear();
    }

    // tslint:disable-next-line: ban-types
    public dataMapping<T>(input: Array<any>, mapping: AbmDataMapping, c: new () => T): Array<T> {

        if (!input || !mapping) {
            throw new Error('No Input Data or mapping');
        }

        if (input.length === 0) {
            return new Array<T>();
        }

        const output: Array<T> = new Array(input.length);

        AbmUtil.checkProperties(input[0], ...mapping.source);

        let sourceKey: string;
        let targetKey: string;
        for (let i = 0; i < input.length; i++) {
            output[i] = new c();
            for (let j = 0; j < mapping.source.length; j++) {
                sourceKey = mapping.source[j];
                targetKey = mapping.target[j];
                output[i][targetKey] = input[i][sourceKey];
            }
        }

        return output;
    }

    public getData(request: AbmRequestLink, ops?: { body?: any, params?: HttpParams }): Observable<AbmResponseData> {

        const fullPath = AbmUtil.formPath(this._useMock ? request.mockFile : request.endpoint, this._apiUrl);
        const options: { headers?: HttpHeaders, params?: HttpParams } = {};

        if (request.header) {
            options.headers = request.header;
        }

        if (ops?.params) {
            options.params = ops.params;
        }

        if (this._useMock) {
            request.method = AbmHttpMethod.GET;
        }

        if (request.method === AbmHttpMethod.GET) {
            return this.http.get<AbmResponseData>(fullPath, options).pipe(map(result => {
                if (!result || result.success === false) {
                    const msg = !result ? '无查询结果' : result.msg;
                    this._countDataApiStatus(request.endpoint, true);
                    throw new Error('Error:' + '[' + request.name + ']' + msg);
                }
                this._countDataApiStatus(request.endpoint, false);
                return result;
            }), catchError(err => {
                this._countDataApiStatus(request.endpoint, true);
                throw err;
            }));
        }

        if (request.method === AbmHttpMethod.POST) {

            let targetUrl: string;
            if (request.replaceBodyWithParam && ops?.body) {
                targetUrl = fullPath + AbmUtil.objectToParamStr(ops.body);
                ops.body = null;
            } else {
                targetUrl = fullPath;
            }

            return this.http.post<AbmResponseData>(targetUrl, ops?.body, options).pipe(map(result => {
                if (!result || result.success === false) {
                    const msg = !result ? '无查询结果' : result.msg;
                    this._countDataApiStatus(request.endpoint, true);
                    throw new Error('Error found:' + '[' + request.name + ']' + msg);
                }
                this._countDataApiStatus(request.endpoint, false);
                return result;
            }), catchError(err => {
                this._countDataApiStatus(request.endpoint, true);
                throw err;
            }));
        }

        return of(null);
    }

    // ////////////////////////// Special Codes, case by case ///////////////////////////////////////
    // 登录
    public login(name: string, password: string): Observable<AbmUser> {
        const params = (new HttpParams())
            .set('loginname', name)
            .set('password', password);

        return this.getData(Global.URL_IF1[0], { params }).pipe(map(data => {
            if (data.code !== 200 && data.code > 0) {
                throw new Error(data.msg);
            }
            const user: AbmUser = this.dataMapping([data.data], this.configService.getMapping('login'), AbmUser)[0];
            user.init(name, password);
            return user;
        }), catchError((err: Error) => {
            throw new Error(err.message);
        }));
    }

    public logout(): Observable<boolean> {
        return this.getData(Global.URL_IF1[1]).pipe(map(data => data.success));
    }

}
